import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { hexToRGBA } from "../../utils";
import {
  ORDER_SIDE_COLORS,
  getOrderStateStyle,
  ORDER_UI_COLORS,
  ORDER_STATE_STYLES,
} from "../../../../utils/orderStyles";
import {
  formatPrice,
  normalizePrice,
  offsetByTicks,
  computeTicksBetween,
  computeReaisDistance,
  computePercentDistance,
} from "../../../../utils/priceUtils";
import { formatNumber } from "../../../../utils/numberFormatting";
import { getOtoTypeLabel } from "../../../../utils/orderConversions";
import { orderReplacementService } from "../../../../utils/orderReplacementService";

// Constants to match main order positioning
const OFFSET_CONFIG = {
  BUTTON_AREA_WIDTH: 220,
  BUTTON_WIDTH: 30,
  BUTTON_HEIGHT: 20,
  SPACING_BETWEEN_LEVELS: 30, // 30px spacing between offset levels
  STOPS_BUTTON_OFFSET: 25, // Vertical offset below main buttons
  STOPS_BUTTON_HEIGHT: 16, // Height of stops button (smaller)
  VERTICAL_PADDING: 5, // Additional padding for collision detection
  OFFSET_FROM_EDGE: 58, // Distance from price scale edge (#119) - must match OrderLine
  LINE_PROXIMITY_THRESHOLD: 12, // 12px above/below = 24px total hit band for connector line drag
  TICKS_BOX_HEIGHT: 16,
  TICKS_BOX_PADDING: 6,
  TICKS_BOX_GAP: 4,
  HOVER_PADDING: 5, // Extra padding around label bounds for easier hover/drag detection
  BUTTON_HOVER_PADDING: 3, // Extra padding around delete button for easier clicks
};

class DraggableOTOStopLine extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dragStartPosition: null,
    };

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.isHover = this.isHover.bind(this);
    this.isHoverForDrag = this.isHoverForDrag.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.renderSVG = this.renderSVG.bind(this);
    this.handleClick = this.handleClick.bind(this);

    // Cache for last drawn position to ensure hover uses same Y as render
    // This prevents position mismatch when yScale changes between draw and hover
    this.lastDrawnState = null;
  }

  componentWillUnmount() {
    // Clear global OTO drag flag if this component started a drag that wasn't completed
    if (this.state.dragStartPosition && window.oTODragInProgress) {
      console.warn(
        "⚠️ DraggableOTOStopLine unmounting with active drag - clearing oTODragInProgress flag",
      );
      window.oTODragInProgress = false;
    }
  }

  // Calculate effective price: if deltaTicks exist and mainOrder present, derive from main price; else use stored price
  getEffectivePrice() {
    const { order, mainOrder } = this.props;
    // Use effective price: price field, or fall back to limit/stop from router orders
    const effectiveMainPrice =
      mainOrder?.price ?? mainOrder?.limit ?? mainOrder?.stop;
    if (order.deltaTicks !== undefined && mainOrder && effectiveMainPrice) {
      return offsetByTicks(order.symbol, effectiveMainPrice, order.deltaTicks);
    }
    // Fall back to stored price, or limit/stop for router orders
    return order.price ?? order.limit ?? order.stop;
  }

  // Collision detection methods copied from OrderLine
  getOrderWidth(orderState) {
    if (orderState === "open") {
      return OFFSET_CONFIG.BUTTON_WIDTH + 80; // trash button + quantity area
    } else {
      return OFFSET_CONFIG.BUTTON_AREA_WIDTH; // full width for local/pending orders
    }
  }

  // Calculate main order's collision offset using the same algorithm as OrderLine
  calculateMainOrderOffset(moreProps, mainOrder, allOrders) {
    if (!moreProps || !moreProps.chartConfig || !moreProps.chartConfig.yScale) {
      return 0;
    }

    const { yScale } = moreProps.chartConfig;
    const currentY = Math.round(yScale(mainOrder.price));

    // Get all orders that would be at the same Y area (vertically overlapping)
    // Use maximum footprint to be conservative since we don't know individual order states here
    const orderFootprint =
      OFFSET_CONFIG.STOPS_BUTTON_OFFSET +
      OFFSET_CONFIG.STOPS_BUTTON_HEIGHT +
      OFFSET_CONFIG.VERTICAL_PADDING;
    const overlappingOrders = allOrders.filter((order) => {
      const orderY = Math.round(yScale(order.price));
      const verticalDistance = Math.abs(currentY - orderY);
      return verticalDistance <= orderFootprint;
    });

    // Sort them by priority (open orders first) and then by Y position
    const sortedOrders = [...overlappingOrders].sort((a, b) => {
      const yA = Math.round(yScale(a.price));
      const yB = Math.round(yScale(b.price));

      const priorityA = a.state === "open" ? 0 : 1;
      const priorityB = b.state === "open" ? 0 : 1;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return yA - yB;
    });

    // Calculate offsets and positions for all overlapping orders in one pass
    const orderOffsets = new Map();
    const offsetToOrder = new Map();
    let cumulativeOffsets = new Map(); // Track cumulative pixel offset for each order

    for (let i = 0; i < sortedOrders.length; i++) {
      const order = sortedOrders[i];

      let offset = 0;
      let positionFound = false;

      // Try offset levels until we find one that doesn't conflict
      while (!positionFound && offset < 10) {
        if (!offsetToOrder.has(offset)) {
          positionFound = true;
        } else {
          offset++;
        }
      }

      orderOffsets.set(order.id, offset);
      offsetToOrder.set(offset, order);

      // Calculate cumulative pixel offset for this order
      let cumulativeOffset = 0;
      for (let level = 0; level < offset; level++) {
        const orderAtLevel = offsetToOrder.get(level);
        if (orderAtLevel) {
          const levelWidth = this.getOrderWidth(orderAtLevel.state);
          cumulativeOffset += levelWidth + OFFSET_CONFIG.SPACING_BETWEEN_LEVELS;
        }
      }
      cumulativeOffsets.set(order.id, cumulativeOffset);
    }

    // Return the cumulative pixel offset for the main order
    return cumulativeOffsets.get(mainOrder.id) || 0;
  }

  handleDragStart(moreProps) {
    const { order } = this.props;
    if (!order) return;

    const {
      chartConfig: { yScale },
    } = moreProps;
    const { mouseXY } = moreProps;
    const [mouseX, mouseY] = mouseXY;

    // Debug: Only log on first drag (when dragStartPosition is null)
    if (!this.state.dragStartPosition) {
      // debug removed (previous temp/effective comparison)
      // debug removed
    }

    const price = this.getTempPrice(); // temp price already accounts for override; based on effective price fallback

    const dy = mouseY - yScale(price);

    this.setState({
      dragStartPosition: { price, dy },
    });

    // Start tracking this drag operation with the service
    const operationId = `oto-stop-${order.id}-${Date.now()}`;
    const startPosition = { x: mouseX, y: mouseY };
    orderReplacementService.startDrag(operationId, startPosition, order);

    // Store operation ID for use in drag complete
    this._currentOperationId = operationId;

    // Set global flag to prevent main order hover during OTO drag
    window.oTODragInProgress = true;
    // debug removed
  }

  handleDrag(moreProps) {
    const { onDrag, order } = this.props;
    const { dragStartPosition } = this.state;

    if (
      !dragStartPosition ||
      !moreProps ||
      !moreProps.mouseXY ||
      !moreProps.chartConfig
    ) {
      return;
    }

    const {
      chartConfig: { yScale },
    } = moreProps;
    const { mouseXY } = moreProps;
    const [, mouseY] = mouseXY;

    // Calculate new price based on mouse position
    const newPrice = yScale.invert(mouseY - dragStartPosition.dy);

    // Round and normalize price using order.symbol directly like profit line
    const normalizedPrice = normalizePrice(order.symbol, newPrice);

    if (onDrag) {
      onDrag(order, normalizedPrice);
    }
  }

  handleDragComplete(moreProps) {
    // debug removed

    const { onDragComplete, order } = this.props;
    const { dragStartPosition } = this.state;

    // Clear global flag when OTO drag completes
    window.oTODragInProgress = false;
    // debug removed

    if (!dragStartPosition || !order) {
      return;
    }

    const {
      chartConfig: { yScale },
    } = moreProps;
    const { mouseXY } = moreProps;
    const [mouseX, mouseY] = mouseXY;

    // Calculate final price
    const finalPrice = yScale.invert(mouseY - dragStartPosition.dy);
    const normalizedPrice = normalizePrice(order.symbol, finalPrice);

    const endPosition = { x: mouseX, y: mouseY };
    const newValues = { price: normalizedPrice };

    // Use centralized service to validate and execute replacement
    const operationId = this._currentOperationId;

    if (operationId) {
      orderReplacementService
        .completeDrag(operationId, endPosition, newValues, "oto-order", () => {
          // This function only executes if validation passes
          if (onDragComplete) {
            onDragComplete({
              order,
              finalPrice: normalizedPrice,
            });
          }
        })
        .then((result) => {
          // ALWAYS notify parent to clear override & drag state, even if no price change
          if (!result && onDragComplete) {
            onDragComplete({ noChanges: true });
          }
        })
        .catch((error) => {
          console.error("❌ OTO stop drag completion failed:", error);
          // Still clear parent state on error
          if (onDragComplete) {
            onDragComplete({ noChanges: true });
          }
        });
    } else {
      console.warn("❌ No operation ID found for OTO stop drag completion");
      // Clear parent state even without operation ID
      if (onDragComplete) {
        onDragComplete({ noChanges: true });
      }
    }

    // Clear drag state and operation ID
    this.setState({
      dragStartPosition: null,
    });
    this._currentOperationId = null;
  }

  getTempPrice() {
    const { order, mainOrder, tempOTOPrices } = this.props;

    // During main order drag, use tempOTOPrices so OTO follows the main
    if (tempOTOPrices && tempOTOPrices.has(`${mainOrder.id}_pending`)) {
      const pendingUpdates = tempOTOPrices.get(`${mainOrder.id}_pending`);
      if (pendingUpdates && pendingUpdates.has(order.id)) {
        const pendingData = pendingUpdates.get(order.id);
        return typeof pendingData === "object"
          ? pendingData.price
          : pendingData;
      }
    }

    // Single source of truth: order.price from the store.
    // During OTO drag, OTOOrdersInteractive overrides order.price with the drag position.
    // At rest, order.price is the confirmed value from the order store.
    // Never use deltaTicks-computed getEffectivePrice() here — deltaTicks can go stale
    // (e.g. after modal edits that update price but not deltaTicks) and produce wrong
    // chart positions.
    return order.price;
  }

  getCanvasDashPattern(strokeDasharray) {
    if (!strokeDasharray || strokeDasharray === "none") {
      return [];
    }
    return strokeDasharray.split(",").map((num) => parseInt(num.trim(), 10));
  }

  getStopColor(mainOrderSide) {
    const otoOrderSide = mainOrderSide === "buy" ? "sell" : "buy";
    return ORDER_SIDE_COLORS[otoOrderSide] || ORDER_SIDE_COLORS.default;
  }

  roundRect(ctx, x, y, width, height, radius) {
    if (width < 2 * radius) radius = width / 2;
    if (height < 2 * radius) radius = height / 2;

    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + width, y, x + width, y + height, radius);
    ctx.arcTo(x + width, y + height, x, y + height, radius);
    ctx.arcTo(x, y + height, x, y, radius);
    ctx.arcTo(x, y, x + width, y, radius);
    ctx.closePath();
  }

  handleClick() {
    const { order, mainOrder, onRemove } = this.props;
    if (!order || !mainOrder) return;

    // We need access to the latest cached button geometry from the last draw.
    // To avoid storing a bunch of transient state, recompute approximate bounds using last known chart metrics.
    // We'll leverage a simple heuristic: detect if mouse was over the delete button via a stored last hover position.
    // Simpler: delegate actual hit detection to isHoverWhenClick by reusing internal drawing math.
    // Since GenericChartComponent already only triggers onClickWhenHover when isHover returned true for the overall line area,
    // we add custom detection for the trash button region by tracking it during last draw.
    if (this._lastButtonBounds && this._lastMousePosition) {
      const { x, y, w, h } = this._lastButtonBounds;
      const { mx, my } = this._lastMousePosition;
      const inside = mx >= x && mx <= x + w && my >= y && my <= y + h;
      if (inside) {
        // debug removed
        try {
          if (window.oTODragInProgress) {
            window.oTODragInProgress = false;
            // debug removed
          }
          // Unified deletion: delegate to parent without passing order.id; parent knows mainOrder context
          if (onRemove) onRemove();
        } catch (e) {
          console.warn("DraggableOTOStopLine: Failed to remove OTO order", e);
        }
      }
    }
  }

  // Calculate label rectangle bounds for hover detection
  getLabelBounds(moreProps) {
    const { order, mainOrder, offsetPixels = 0, allOrders = [] } = this.props;

    if (!order || !mainOrder) return { x: 0, y: 0, width: 0, height: 0 };

    const {
      chartConfig: { width, yScale },
    } = moreProps;

    if (!yScale || typeof yScale !== "function") {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    // Calculate main order's collision offset using the same algorithm as drawOnCanvas
    let calculatedOffset = offsetPixels;
    if (allOrders.length > 0) {
      try {
        calculatedOffset = this.calculateMainOrderOffset(
          moreProps,
          mainOrder,
          allOrders,
        );
      } catch {
        calculatedOffset = offsetPixels;
      }
    }

    const currentPrice = this.getTempPrice();

    // Use cached Y position if available and order hasn't changed
    // This ensures hover detection uses the same Y that was rendered
    let y;
    if (
      this.lastDrawnState &&
      this.lastDrawnState.orderId === order.id &&
      this.lastDrawnState.price === currentPrice
    ) {
      y = this.lastDrawnState.y;
    } else {
      // Fallback to calculation (first hover before any draw)
      y = yScale(currentPrice);
    }
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;

    // Calculate positions using the same logic as drawOnCanvas
    let backgroundRightEdge;

    if (mainOrder.state === "open") {
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      const trashButtonX =
        width - openOrderWidth - offsetFromEdge - calculatedOffset;
      backgroundRightEdge = trashButtonX + openOrderWidth + 2;
    } else {
      // Calculate trash button position (not used directly but needed for backgroundRightEdge calculation)
      width -
        OFFSET_CONFIG.BUTTON_AREA_WIDTH -
        offsetFromEdge -
        calculatedOffset;
      const sendButtonX =
        width -
        OFFSET_CONFIG.BUTTON_WIDTH -
        10 -
        offsetFromEdge -
        calculatedOffset;
      const sendButtonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
      const sendButtonRightEdge = sendButtonX + sendButtonWidth;
      backgroundRightEdge = sendButtonRightEdge + 2;
    }

    // Label dimensions calculation (matching drawOnCanvas)
    const displayPrice = this.getTempPrice();
    const labelText = `${getOtoTypeLabel("stop")} ${formatPrice(order.symbol, displayPrice)}`;

    // Create temporary canvas context to measure text
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font =
      "600 10px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    const textMetrics = ctx.measureText(labelText);
    const textWidth = textMetrics.width;

    const labelPadding = 8;
    const labelWidth = textWidth + labelPadding * 2;
    const labelHeight = 20;

    const buttonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
    const buttonPadding = 4;

    // Info box dimensions (matching drawOnCanvas - includes ticks, R$, %)
    const mainPrice = mainOrder.price;
    const ticksCount = computeTicksBetween(
      order.symbol,
      mainPrice,
      displayPrice,
    );
    let infoText =
      ticksCount !== undefined ? `${Math.abs(ticksCount)} tks` : "";
    if (infoText) {
      const reaisAmount = computeReaisDistance(
        order.symbol,
        mainPrice,
        displayPrice,
        order.quantity,
      );
      if (reaisAmount !== undefined) {
        infoText += ` · R$ ${formatNumber(Math.abs(reaisAmount), 2)}`;
      }
      const percentAmount = computePercentDistance(mainPrice, displayPrice);
      if (percentAmount !== undefined) {
        infoText += ` · ${Math.abs(percentAmount).toFixed(1)}%`;
      }
    }
    const ticksText = infoText;
    ctx.font =
      "500 9px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    const ticksTextWidth = ticksText ? ctx.measureText(ticksText).width : 0;
    const ticksBoxWidth = ticksText
      ? ticksTextWidth + OFFSET_CONFIG.TICKS_BOX_PADDING * 2
      : 0;
    const ticksBoxTotalWidth = ticksText
      ? ticksBoxWidth + OFFSET_CONFIG.TICKS_BOX_GAP
      : 0;

    // Layout: [X] | [Info] | [label]
    const labelX = backgroundRightEdge - labelWidth;
    const labelY = y - labelHeight / 2;

    let ticksBoxX;
    if (ticksText) {
      ticksBoxX = labelX - OFFSET_CONFIG.TICKS_BOX_GAP - ticksBoxWidth;
    }

    // For local orders, include button area in bounds for click detection
    if (mainOrder.state === "local") {
      const leftElement = ticksText ? ticksBoxX : labelX;
      const buttonX = leftElement - buttonPadding - buttonWidth;
      return {
        x: buttonX,
        y: labelY,
        width: backgroundRightEdge - buttonX,
        height: labelHeight,
      };
    }

    // For open orders (no delete button shown), include info box in bounds
    const leftEdge = ticksText ? ticksBoxX : labelX;
    return {
      x: leftEdge,
      y: labelY,
      width: backgroundRightEdge - leftEdge,
      height: labelHeight,
    };
  }

  // Calculate connector line bounds for hover detection (matches drawOnCanvas geometry)
  getConnectorLineBounds(moreProps) {
    const { order, mainOrder, offsetPixels = 0, allOrders = [] } = this.props;
    if (!order || !mainOrder) return null;

    const {
      chartConfig: { width, yScale },
    } = moreProps;
    if (!yScale || typeof yScale !== "function") return null;

    let calculatedOffset = offsetPixels;
    if (allOrders.length > 0) {
      try {
        calculatedOffset = this.calculateMainOrderOffset(
          moreProps,
          mainOrder,
          allOrders,
        );
      } catch {
        calculatedOffset = offsetPixels;
      }
    }

    const currentPrice = this.getTempPrice();
    let y;
    if (
      this.lastDrawnState &&
      this.lastDrawnState.orderId === order.id &&
      this.lastDrawnState.price === currentPrice
    ) {
      y = this.lastDrawnState.y;
    } else {
      y = yScale(currentPrice);
    }
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;

    // Calculate backgroundRightEdge (same as drawOnCanvas)
    let backgroundRightEdge;
    if (mainOrder.state === "open") {
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      const trashButtonX =
        width - openOrderWidth - offsetFromEdge - calculatedOffset;
      backgroundRightEdge = trashButtonX + openOrderWidth + 2;
    } else {
      const sendButtonX =
        width -
        OFFSET_CONFIG.BUTTON_WIDTH -
        10 -
        offsetFromEdge -
        calculatedOffset;
      const sendButtonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
      backgroundRightEdge = sendButtonX + sendButtonWidth + 2;
    }

    // Label dimensions (same as drawOnCanvas)
    const displayPrice = this.getTempPrice();
    const labelText = `${getOtoTypeLabel("stop")} ${formatPrice(order.symbol, displayPrice)}`;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font =
      "600 10px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    const textWidth = ctx.measureText(labelText).width;
    const labelPadding = 8;
    const labelWidth = textWidth + labelPadding * 2;
    const buttonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
    const buttonPadding = 4;

    // Info box dimensions (same as drawOnCanvas - includes ticks, R$, %)
    const mainPrice = mainOrder.price;
    const ticksCount = computeTicksBetween(
      order.symbol,
      mainPrice,
      displayPrice,
    );
    let infoText =
      ticksCount !== undefined ? `${Math.abs(ticksCount)} tks` : "";
    if (infoText) {
      const reaisAmount = computeReaisDistance(
        order.symbol,
        mainPrice,
        displayPrice,
        order.quantity,
      );
      if (reaisAmount !== undefined) {
        infoText += ` · R$ ${formatNumber(Math.abs(reaisAmount), 2)}`;
      }
      const percentAmount = computePercentDistance(mainPrice, displayPrice);
      if (percentAmount !== undefined) {
        infoText += ` · ${Math.abs(percentAmount).toFixed(1)}%`;
      }
    }
    const ticksText = infoText;
    ctx.font =
      "500 9px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    const ticksTextWidth = ticksText ? ctx.measureText(ticksText).width : 0;
    const ticksBoxWidth = ticksText
      ? ticksTextWidth + OFFSET_CONFIG.TICKS_BOX_PADDING * 2
      : 0;
    const ticksBoxTotalWidth = ticksText
      ? ticksBoxWidth + OFFSET_CONFIG.TICKS_BOX_GAP
      : 0;

    // Layout: [X] | [Info] | [label]
    const labelX = backgroundRightEdge - labelWidth;

    let ticksBoxX;
    if (ticksText) {
      ticksBoxX = labelX - OFFSET_CONFIG.TICKS_BOX_GAP - ticksBoxWidth;
    }

    // Connector line: from verticalX (mainBackgroundX) to leftmost visual element
    let mainBackgroundX;
    if (mainOrder.state === "open") {
      const mainOpenOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      const mainTrashButtonX =
        width - mainOpenOrderWidth - 8 - calculatedOffset;
      mainBackgroundX = mainTrashButtonX - 2;
    } else {
      const mainTrashButtonX =
        width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - 8 - calculatedOffset;
      mainBackgroundX = mainTrashButtonX - 2;
    }

    let endX;
    if (mainOrder.state === "local") {
      const leftElement = ticksText ? ticksBoxX : labelX;
      endX = leftElement - buttonPadding - buttonWidth;
    } else {
      endX = ticksText ? ticksBoxX : labelX;
    }
    return { startX: mainBackgroundX, endX, y };
  }

  // Check if mouse is hovering over the draggable area (only the label rectangle)
  isHoverForDrag(moreProps) {
    const { order, mainOrder } = this.props;

    // OTO should be draggable if it's local or if main order is open
    if (!order || !mainOrder) {
      return false;
    }

    const isDraggable =
      order.state === "local" || !order.state || mainOrder.state === "open";
    if (!isDraggable) {
      return false;
    }

    const { mouseXY } = moreProps;
    if (!mouseXY) return false;

    const [mouseX, mouseY] = mouseXY;

    // Get label rectangle bounds
    const labelBounds = this.getLabelBounds(moreProps);
    const hp = OFFSET_CONFIG.HOVER_PADDING;

    // Check if hovering over the label rectangle (expanded by HOVER_PADDING on all sides)
    return (
      mouseX >= labelBounds.x - hp &&
      mouseX <= labelBounds.x + labelBounds.width + hp &&
      mouseY >= labelBounds.y - hp &&
      mouseY <= labelBounds.y + labelBounds.height + hp
    );
  }

  isHover(moreProps) {
    const { order, mainOrder } = this.props;

    // OTO should be hoverable if it's draggable (local OTO or open main order)
    if (!order || !mainOrder) {
      return false;
    }

    // Use the same logic as render() for consistency
    const isDraggable =
      order.state === "local" || !order.state || mainOrder.state === "open";
    if (!isDraggable) {
      return false;
    }

    const { mouseXY } = moreProps;
    if (!mouseXY) return false;

    const [mouseX, mouseY] = mouseXY;

    // --- Zone 1: Connector line drag zone ---
    const connectorBounds = this.getConnectorLineBounds(moreProps);
    if (connectorBounds) {
      const { startX, endX, y: lineY } = connectorBounds;
      if (
        mouseX >= startX &&
        mouseX < endX &&
        Math.abs(mouseY - lineY) <= OFFSET_CONFIG.LINE_PROXIMITY_THRESHOLD
      ) {
        return true;
      }
    }

    // --- Zone 2: Label area with hover padding for easier targeting ---
    const labelBounds = this.getLabelBounds(moreProps);
    if (!labelBounds) return false;

    const {
      x: labelX,
      y: labelY,
      width: labelWidth,
      height: labelHeight,
    } = labelBounds;
    const hp = OFFSET_CONFIG.HOVER_PADDING;

    // Check if hovering within the label rectangle (expanded by HOVER_PADDING on all sides)
    const inLabelArea =
      mouseX >= labelX - hp &&
      mouseX <= labelX + labelWidth + hp &&
      mouseY >= labelY - hp &&
      mouseY <= labelY + labelHeight + hp;

    if (!inLabelArea) {
      return false;
    }

    // EXCLUDE delete button area from drag detection (for local main orders)
    if (mainOrder.state === "local" && this._lastButtonBounds) {
      const { x: btnX, y: btnY, w: btnW, h: btnH } = this._lastButtonBounds;
      const inDeleteButton =
        mouseX >= btnX &&
        mouseX <= btnX + btnW &&
        mouseY >= btnY &&
        mouseY <= btnY + btnH;

      if (inDeleteButton) {
        return false; // Don't allow drag on delete button
      }
    }

    return true;
  }

  drawOnCanvas(ctx, moreProps) {
    const { order, mainOrder, offsetPixels = 0, allOrders = [] } = this.props;

    if (!order || !mainOrder) return;

    const {
      chartConfig: { width, yScale },
    } = moreProps;

    if (!yScale || typeof yScale !== "function") {
      return;
    }

    // Calculate main order's collision offset using the same algorithm as OrderLine
    let calculatedOffset = offsetPixels; // Use passed offset as fallback
    if (allOrders.length > 0) {
      try {
        calculatedOffset = this.calculateMainOrderOffset(
          moreProps,
          mainOrder,
          allOrders,
        );
      } catch (error) {
        console.warn(
          "Failed to calculate main order offset, using fallback:",
          error,
        );
        calculatedOffset = offsetPixels;
      }
    }

    const currentPrice = this.getTempPrice(); // getTempPrice() already handles effective price fallback
    const y = yScale(currentPrice);
    const mainY = yScale(mainOrder.price);

    // Cache drawn position for consistent hover detection
    // This prevents position mismatch when yScale changes between draw and hover
    this.lastDrawnState = {
      y,
      price: currentPrice,
      orderId: order.id,
    };
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;

    const stateStyle = getOrderStateStyle(mainOrder.state);
    const dashPattern = this.getCanvasDashPattern(stateStyle.strokeDasharray);

    ctx.setLineDash(dashPattern);
    ctx.lineWidth = 1;

    let trashButtonX, sendButtonX, backgroundRightEdge;

    if (mainOrder.state === "open") {
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      trashButtonX = width - openOrderWidth - offsetFromEdge - calculatedOffset;
      sendButtonX =
        width -
        OFFSET_CONFIG.BUTTON_WIDTH -
        10 -
        offsetFromEdge -
        calculatedOffset;
      backgroundRightEdge = trashButtonX + openOrderWidth + 2;
    } else {
      trashButtonX =
        width -
        OFFSET_CONFIG.BUTTON_AREA_WIDTH -
        offsetFromEdge -
        calculatedOffset;
      sendButtonX =
        width -
        OFFSET_CONFIG.BUTTON_WIDTH -
        10 -
        offsetFromEdge -
        calculatedOffset;
      const sendButtonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
      const sendButtonRightEdge = sendButtonX + sendButtonWidth;
      backgroundRightEdge = sendButtonRightEdge + 2;
    }

    const displayPrice = this.getTempPrice(); // getTempPrice() already handles effective price fallback
    const labelText = `${getOtoTypeLabel("stop")} ${formatPrice(order.symbol, displayPrice)}`;
    ctx.font =
      "600 10px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    const textMetrics = ctx.measureText(labelText);
    const textWidth = textMetrics.width;
    const labelPadding = 8;
    const labelWidth = textWidth + labelPadding * 2;
    const labelHeight = 20;

    const buttonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
    const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
    const buttonPadding = 4;

    // Compute ticks distance box dimensions with R$ and % info
    const mainPrice = mainOrder.price;
    const ticksCount = computeTicksBetween(
      order.symbol,
      mainPrice,
      displayPrice,
    );
    let infoText =
      ticksCount !== undefined ? `${Math.abs(ticksCount)} tks` : "";
    if (infoText) {
      const reaisAmount = computeReaisDistance(
        order.symbol,
        mainPrice,
        displayPrice,
        order.quantity,
      );
      if (reaisAmount !== undefined) {
        infoText += ` · R$ ${formatNumber(Math.abs(reaisAmount), 2)}`;
      }
      const percentAmount = computePercentDistance(mainPrice, displayPrice);
      if (percentAmount !== undefined) {
        infoText += ` · ${Math.abs(percentAmount).toFixed(1)}%`;
      }
    }
    const ticksText = infoText;
    const ticksBoxFont =
      "500 9px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    ctx.font = ticksBoxFont;
    const ticksTextWidth = ticksText ? ctx.measureText(ticksText).width : 0;
    const ticksBoxWidth = ticksText
      ? ticksTextWidth + OFFSET_CONFIG.TICKS_BOX_PADDING * 2
      : 0;
    const ticksBoxTotalWidth = ticksText
      ? ticksBoxWidth + OFFSET_CONFIG.TICKS_BOX_GAP
      : 0;

    // Layout: [X] | [Info] | [label]   (for local with info)
    //         [X] | [label]             (for local without info)
    //         [Info] | [label]          (for open with info)
    //         [label]                   (for open without info)
    const labelX = backgroundRightEdge - labelWidth;
    const labelY = y - labelHeight / 2;

    let ticksBoxX;
    if (ticksText) {
      ticksBoxX = labelX - OFFSET_CONFIG.TICKS_BOX_GAP - ticksBoxWidth;
    }
    const ticksBoxHeight = labelHeight; // Match main label height
    const ticksBoxY = y - ticksBoxHeight / 2;

    // Button position: to the left of ticks box (or label if no ticks), only for local orders
    let buttonX;
    if (mainOrder.state === "local") {
      const leftElement = ticksText ? ticksBoxX : labelX;
      buttonX = leftElement - buttonPadding - buttonWidth;
    }
    const buttonY = y - buttonHeight / 2;

    let mainBackgroundX;
    if (mainOrder.state === "open") {
      const mainOpenOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      const mainTrashButtonX =
        width - mainOpenOrderWidth - 8 - calculatedOffset;
      mainBackgroundX = mainTrashButtonX - 2;
    } else {
      const mainTrashButtonX =
        width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - 8 - calculatedOffset;
      mainBackgroundX = mainTrashButtonX - 2;
    }

    const verticalX = mainBackgroundX;
    const horizontalStartX = verticalX;
    // Connector ends at the leftmost visual element
    let horizontalEndX;
    if (mainOrder.state === "local") {
      horizontalEndX = buttonX;
    } else if (ticksText) {
      horizontalEndX = ticksBoxX;
    } else {
      horizontalEndX = labelX;
    }

    const stopColor = this.getStopColor(mainOrder.side);
    ctx.strokeStyle = stopColor;

    // Draw vertical connector - stop at box edges for clean appearance
    const mainHalfHeight = 12; // main order box is 24px tall (backgroundHeight in OrderLine)
    const otoHalfHeight = labelHeight / 2;
    let vStartY, vEndY;
    if (y < mainY) {
      // OTO is above main order
      vStartY = y + otoHalfHeight;
      vEndY = mainY - mainHalfHeight;
    } else {
      // OTO is below main order
      vStartY = mainY + mainHalfHeight;
      vEndY = y - otoHalfHeight;
    }
    if (vEndY > vStartY) {
      ctx.beginPath();
      ctx.moveTo(verticalX, vStartY);
      ctx.lineTo(verticalX, vEndY);
      ctx.stroke();
    }

    // Draw horizontal line from label right edge to Y-axis (price scale)
    ctx.beginPath();
    ctx.moveTo(backgroundRightEdge, y);
    ctx.lineTo(width, y);
    ctx.stroke();

    // Draw label background
    const bgOpacity = ORDER_STATE_STYLES.local.opacity;
    const bgColor = hexToRGBA(stopColor, bgOpacity);
    ctx.fillStyle = bgColor;
    this.roundRect(ctx, labelX, labelY, labelWidth, labelHeight, 3);
    ctx.fill();

    // Draw label border
    ctx.strokeStyle = stopColor;
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    this.roundRect(ctx, labelX, labelY, labelWidth, labelHeight, 3);
    ctx.stroke();

    // Draw label text
    ctx.fillStyle = "#ffffff";
    ctx.font =
      "600 10px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(labelText, labelX + labelWidth / 2, labelY + labelHeight / 2);

    // Draw ticks distance box
    if (ticksText) {
      const ticksBgColor = hexToRGBA(stopColor, bgOpacity * 0.8);
      ctx.fillStyle = ticksBgColor;
      this.roundRect(
        ctx,
        ticksBoxX,
        ticksBoxY,
        ticksBoxWidth,
        ticksBoxHeight,
        3,
      );
      ctx.fill();

      ctx.strokeStyle = stopColor;
      ctx.lineWidth = 1;
      this.roundRect(
        ctx,
        ticksBoxX,
        ticksBoxY,
        ticksBoxWidth,
        ticksBoxHeight,
        3,
      );
      ctx.stroke();

      ctx.fillStyle = "#ffffff";
      ctx.font = ticksBoxFont;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        ticksText,
        ticksBoxX + ticksBoxWidth / 2,
        ticksBoxY + ticksBoxHeight / 2,
      );
    }

    // Draw delete button for local orders
    if (mainOrder.state === "local") {
      let isHovering = false;
      if (moreProps && moreProps.mouseXY) {
        const [mouseX, mouseY] = moreProps.mouseXY;
        isHovering =
          mouseX >= buttonX &&
          mouseX <= buttonX + buttonWidth &&
          mouseY >= buttonY &&
          mouseY <= buttonY + buttonHeight;
        // Track last mouse position for click handler to test bounds
        this._lastMousePosition = { mx: mouseX, my: mouseY };
      }

      const backgroundColor = hexToRGBA(
        ORDER_UI_COLORS.trashButton,
        isHovering ? 1.0 : 0.8,
      );
      this.roundRect(ctx, buttonX, buttonY, buttonWidth, buttonHeight, 3);
      ctx.fillStyle = backgroundColor;
      ctx.fill();

      ctx.fillStyle = hexToRGBA(ORDER_UI_COLORS.textPrimary, 0.9);
      ctx.font =
        "600 10px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.shadowColor = ORDER_UI_COLORS.textShadow;
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;

      ctx.fillText("×", buttonX + buttonWidth / 2, buttonY + buttonHeight / 2);

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Cache button bounds for later click detection
      this._lastButtonBounds = {
        x: buttonX,
        y: buttonY,
        w: buttonWidth,
        h: buttonHeight,
      };
    }

    ctx.setLineDash([]);
    ctx.lineWidth = 1;
  }

  renderSVG() {
    return null;
  }

  // Check if mouse is hovering over the delete button (for pointer cursor)
  isHoverButton(moreProps) {
    const { mainOrder } = this.props;

    // Only show pointer cursor for local main orders (which have delete button)
    if (!mainOrder || mainOrder.state !== "local") {
      return false;
    }

    if (!this._lastButtonBounds) {
      return false;
    }

    const { mouseXY } = moreProps;
    if (!mouseXY) return false;

    const [mouseX, mouseY] = mouseXY;
    const { x: btnX, y: btnY, w: btnW, h: btnH } = this._lastButtonBounds;
    const bp = OFFSET_CONFIG.BUTTON_HOVER_PADDING;

    return (
      mouseX >= btnX - bp &&
      mouseX <= btnX + btnW + bp &&
      mouseY >= btnY - bp &&
      mouseY <= btnY + btnH + bp
    );
  }

  render() {
    const { order, mainOrder } = this.props;
    // OTO should be draggable if:
    // 1. OTO order itself is local (not sent yet)
    // 2. Main order is open (we have router sync capability for open orders)
    const isDraggable =
      order.state === "local" || !order.state || mainOrder.state === "open";

    // Show delete button only for local main orders
    const showDeleteButton = mainOrder && mainOrder.state === "local";

    return (
      <g>
        {/* Main draggable component with ns-resize cursor */}
        <GenericChartComponent
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          drawOn={["mousemove", "pan", "drag", "zoom", "xAxisZoom", "resize"]}
          svgDraw={this.renderSVG}
          onClickWhenHover={this.handleClick}
          isHover={this.isHover}
          onDragStart={isDraggable ? this.handleDragStart : undefined}
          onDrag={isDraggable ? this.handleDrag : undefined}
          onDragComplete={isDraggable ? this.handleDragComplete : undefined}
          selected={isDraggable}
          interactiveCursorClass={
            isDraggable ? "react-stockcharts-ns-resize-cursor" : undefined
          }
        />
        {/* Delete button overlay with pointer cursor and click handler */}
        {showDeleteButton && (
          <GenericChartComponent
            key="btn"
            canvasToDraw={getMouseCanvas}
            canvasDraw={() => {}}
            drawOn={["mousemove"]}
            isHover={this.isHoverButton.bind(this)}
            onClickWhenHover={this.handleClick}
            selected={true}
            interactiveCursorClass="react-stockcharts-pointer-cursor"
          />
        )}
      </g>
    );
  }
}

DraggableOTOStopLine.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
    // type is optional now; classification derived dynamically
    type: PropTypes.string,
    symbol: PropTypes.string.isRequired,
    state: PropTypes.string, // Local or sent state
    deltaTicks: PropTypes.number, // Number of steps from main order
  }).isRequired,
  mainOrder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
  }).isRequired,
  symbol: PropTypes.string.isRequired,
  allOrders: PropTypes.array,
  tempOTOPrices: PropTypes.instanceOf(Map),
  offsetPixels: PropTypes.number,
  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  onRemove: PropTypes.func,
};

DraggableOTOStopLine.defaultProps = {
  allOrders: [],
  tempOTOPrices: new Map(),
  offsetPixels: 0,
  onDrag: () => {},
  onDragComplete: () => {},
  onRemove: () => {},
};

export default DraggableOTOStopLine;
